import { theme } from '@acorns/grove'
import { AcornsIcon } from '@acorns/icons'
import { Button } from '@acorns/web-components'
import styled from 'styled-components'

export const PageContainer = styled.div`
  background: ${theme.colors.primaryWhite};
  box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  max-width: 800px;
  margin: 0 auto;
  height: auto;
  padding-top: 50px;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    min-height: 100vh;
  }
`

export const ScrollFadeWrapper = styled.div<{ offsetHeight: number }>`
  position: relative;
  width: 100%;
  margin: 0 auto;

  @media (min-width: ${theme.dimensions.breakpointsSmall + 1}px) {
    max-width: 100%;
    height: calc(100vh - 350px);
  }

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    max-width: 100%;
    height: calc(100vh - 200px);
    margin-bottom: ${(props) => props.offsetHeight}px;
    padding: 0 30px;
    box-sizing: border-box;
  }
`

export const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`

export const ScrollContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    gap: 16px;
  }
`

export const StyledHeadline = styled.h2`
  font-weight: 600;
  font-size: 26px;
  line-height: 41px;
  color: ${theme.colors.primarySlate};
  text-align: center;
  margin-bottom: 0;
  margin-top: 0;
`

export const StyledSubtitle = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.primarySlate};
  text-align: center;
  margin-bottom: 10px;
  margin-top: -20px;
`

export const StyledSubmitButton = styled(Button)`
  margin: 0 auto;
  min-width: 220px;

  @media (min-width: ${theme.dimensions.breakpointsSmall + 1}px) {
    min-width: 340px;
  }

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    min-width: 0;
    width: 100%;
    max-width: 340px;
  }
`

export const ComparePlansButton = styled(Button)`
  margin: 0;
  padding-top: 20px;
  color: ${theme.colors.naturalFern};
`

export const ButtonFooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: center;
  width: 100%;
  background-color: ${theme.colors.primaryWhite};
  position: relative;
  z-index: 10;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 20px 30px calc(20px + env(safe-area-inset-bottom, 0));
  }

  @media (min-width: ${theme.dimensions.breakpointsSmall + 1}px) {
    padding: 30px 0;
    border-top: 1px solid ${theme.colors.primaryIvory};
    border-bottom-left-radius: ${theme.borderRadius.large};
    border-bottom-right-radius: ${theme.borderRadius.large};
  }
`

export const Cards = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 391px;
  margin: 0 auto;
  padding: 0;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    max-width: 360px;
    width: 100%;
  }
`

export const PlanCardContainer = styled.div<{
  tierKey: string
  selected?: boolean
  gold?: boolean
  expanded?: boolean
}>`
  width: 333px;
  box-sizing: border-box;
  background: ${({ selected, gold }) =>
    selected
      ? gold
        ? 'rgba(242, 188, 39, 0.05)'
        : '#f4fcf7'
      : `${theme.colors.primaryWhite}`};
  border: ${({ selected, gold }) =>
    selected
      ? gold
        ? '3px solid #B88A09'
        : `3px solid ${theme.colors.primaryGreen}`
      : '3px solid rgba(204, 207, 207, 0.5)'};
  box-shadow: ${({ selected, gold }) =>
    selected
      ? gold
        ? '0 3px 15px 2px rgba(184, 138, 9, 0.08)'
        : '0 3px 15px 2px rgba(46, 173, 79, 0.08)'
      : '0 3px 15px 2px rgba(0,0,0,0.02)'};
  border-radius: 25px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  z-index: ${({ expanded }) => (expanded ? 2 : 0)};
  padding: 0;
  max-width: 333px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    width: 100%;
    max-width: 360px;
    border-radius: 20px;
  }
`

export const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 0 20px;
`

export const PlanHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

export const PlanName = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: ${theme.colors.primarySlate};
`

export const PlanDesc = styled.div<{ selected?: boolean }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ selected }) =>
    selected ? `${theme.colors.primarySlate}` : `${theme.colors.primaryStone}`};
  width: 259px;
`

export const PlanPriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 20px;
  padding-bottom: 20px;
`

export const PlanRadio = styled.div<{
  selected?: boolean
  gold?: boolean
}>`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ selected, gold }) =>
    selected
      ? gold
        ? '#DCA613'
        : theme.colors.primaryForest
      : `${theme.colors.primaryWhite}`};
  border: ${({ selected, gold }) =>
    selected
      ? gold
        ? '2.5px solid #DCA613'
        : `2.5px solid ${theme.colors.primaryForest}`
      : '2px solid #CCCFCF'};
  box-shadow: ${({ selected }) =>
    selected ? '0 2px 8px 0 rgba(0,0,0,0.04)' : 'none'};
  cursor: pointer;
  transition:
    border 0.2s,
    background 0.2s;

  svg {
    display: block;
  }
`

export const PlanPrice = styled.div<{
  selected?: boolean
  gold?: boolean
}>`
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  padding-left: 20px;
  margin-top: 8px;
  color: ${({ selected, gold }) =>
    selected
      ? gold
        ? '#B88A09'
        : `${theme.colors.primaryForest}`
      : `${theme.colors.primaryStone}`};

  strong {
    font-weight: 600;
    color: inherit;
  }

  span {
    font-weight: 600;
    margin-left: 6px;
    color: ${({ selected, gold }) =>
      selected
        ? gold
          ? '#B88A0999'
          : 'rgba(56, 134, 14, 0.6)'
        : `${theme.colors.primaryStoneLight}`};
  }
`

export const KeyBenefitsButton = styled.button<{
  selected?: boolean
  gold?: boolean
  expanded?: boolean
}>`
  background: ${theme.colors.primaryIvory};
  border-radius: 50px;
  padding: 6px 10px 6px 15px;

  font-weight: 600;
  font-size: 12px;
  color: ${theme.colors.primarySlate};
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 6px;
  transition: background 0.2s;

  ${({ selected, gold }) =>
    selected &&
    gold &&
    `
      color: ${theme.colors.naturalMushroom};
      background: rgba(220, 166, 19, 0.15);
    `}

  ${({ selected, gold }) =>
    selected &&
    !gold &&
    `
      color: ${theme.colors.naturalLightOak};
      background: rgba(56, 134, 14, 0.10);
    `}
`

export const PlanBenefits = styled.div`
  padding: 20px 20px 0 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0;
`

export const PlanIncludes = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${theme.colors.primarySlate};
  margin-bottom: 8px;
`

export const PlanBenefitsList = styled.ul`
  padding-left: 0;
  margin-left: 5px;
  margin-top: 0px;
  margin-bottom: 0;
  padding-bottom: 20px;
  list-style: none;
  width: 100%;
`

export const PlanBenefitItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`

export const PlanBenefitCheckContainer = styled.div`
  align-self: flex-start;
`

export const PlanBenefitCheck = styled(AcornsIcon)<{
  selected?: boolean
  gold?: boolean
}>`
  width: 14px;
  height: 14px;
  margin-top: 5px;
  margin-right: 8px;

  path[stroke] {
    stroke: ${({ selected, gold }) =>
      selected
        ? gold
          ? '#B88A09'
          : `${theme.colors.adaDarkGreen}`
        : `${theme.colors.primaryStoneLight}`};
  }
`

export const PlanBenefitText = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: ${theme.colors.primarySlate};
  line-height: 22px;
`

export const StyledChevron = styled(AcornsIcon)<{
  selected?: boolean
  gold?: boolean
  expanded?: boolean
}>`
  margin-left: 4px;
  width: 8px;
  height: 8px;
  path {
    stroke: ${({ selected, gold }) =>
      selected
        ? gold
          ? 'rgba(120, 94, 70, 0.75)'
          : 'rgba(72, 107, 77, 0.75)'
        : '#6F6F6F'};
    transition: stroke 0.2s;
  }
  transform: ${({ expanded }) => (expanded ? 'none' : 'rotate(180deg)')};
  transition: transform 0.2s;
`

export const FinePrint = styled.div`
  font-size: 12px;
  color: ${theme.colors.primaryStone};
  width: 321px;
  text-align: left;
  margin: 0 auto 80px;

  @media (max-width: ${theme.dimensions.breakpointsSmall}px) {
    width: 100%;
    padding: 0;
    box-sizing: border-box;
    margin-bottom: 35%;
  }
`

export const Fade = styled.div<{ position: 'top' | 'bottom' }>`
  position: sticky;
  ${({ position }) => position}: 0;
  top: -1px;
  height: 45px;
  pointer-events: none;
  z-index: 9;
  margin-top: ${({ position }) => (position === 'top' ? '-40px' : '0')};
  background: linear-gradient(
    to ${(p) => (p.position === 'top' ? 'bottom' : 'top')},
    ${theme.colors.primaryWhite} 0%,
    rgba(255, 255, 255, 0) 100%
  );

  ${(p) =>
    p.position === 'bottom' &&
    `
    @media (min-width: ${theme.dimensions.breakpointsSmall + 1}px) {
      display: none;
    }
  `}
`

export const SpinnerContainer = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`
